/**
 * Google Analytics 4 — caricamento condizionato al consenso cookie.
 * Conforme al GDPR e alle linee guida del Garante italiano:
 *  - GA4 si carica SOLO se l'utente ha cliccato "Accetta" nel cookie banner
 *  - IP anonymization attiva
 *  - Google Signals e ad personalization disabilitati
 *  - Tracking dei cambi di rotta nell'SPA
 */
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

const MEASUREMENT_ID = "G-V9ELCN0F79";
const CONSENT_STORAGE_KEY = "mc_cookie_consent_v1";
const CONSENT_EVENT = "mc:cookie-consent-changed";

function hasAnalyticsConsent() {
    try {
        const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
        if (!raw) return false;
        const parsed = JSON.parse(raw);
        return Boolean(parsed.analytics);
    } catch {
        return false;
    }
}

function loadGtagScript() {
    if (document.getElementById("ga4-script")) return; // già caricato

    // Inizializza dataLayer e gtag PRIMA del caricamento dello script
    window.dataLayer = window.dataLayer || [];
    window.gtag = function () { window.dataLayer.push(arguments); };
    window.gtag("js", new Date());

    // Consent Mode v2 — default conservativo (anonimo)
    window.gtag("consent", "default", {
        ad_storage: "denied",
        ad_user_data: "denied",
        ad_personalization: "denied",
        analytics_storage: "granted", // l'utente ha già accettato
        functionality_storage: "granted",
        security_storage: "granted",
    });

    window.gtag("config", MEASUREMENT_ID, {
        anonymize_ip: true,
        allow_google_signals: false,
        allow_ad_personalization_signals: false,
        send_page_view: false, // gestito manualmente dal hook per supportare SPA
    });

    const script = document.createElement("script");
    script.id = "ga4-script";
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
    document.head.appendChild(script);
}

function trackPageView(pathname, title) {
    if (typeof window.gtag !== "function") return;
    window.gtag("event", "page_view", {
        page_location: window.location.origin + pathname,
        page_path: pathname,
        page_title: title || document.title,
    });
}

export function useAnalytics() {
    const location = useLocation();
    const consentApplied = useRef(false);

    // 1) Carica GA4 quando l'utente dà il consenso (o se già dato in sessione precedente)
    useEffect(() => {
        const applyIfConsent = () => {
            if (hasAnalyticsConsent() && !consentApplied.current) {
                loadGtagScript();
                consentApplied.current = true;
                // Track della pagina corrente subito dopo l'attivazione
                trackPageView(location.pathname, document.title);
            }
        };

        applyIfConsent();
        window.addEventListener(CONSENT_EVENT, applyIfConsent);
        return () => window.removeEventListener(CONSENT_EVENT, applyIfConsent);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // 2) Track dei cambi di rotta nell'SPA (solo se GA4 è già caricato)
    useEffect(() => {
        if (!consentApplied.current) return;
        // Piccolo delay per assicurarsi che document.title sia stato aggiornato da useSEO
        const t = setTimeout(() => {
            trackPageView(location.pathname, document.title);
        }, 250);
        return () => clearTimeout(t);
    }, [location.pathname]);
}

/** Funzione utilitaria chiamata dal CookieBanner quando l'utente fa una scelta. */
export function notifyConsentChange() {
    window.dispatchEvent(new CustomEvent(CONSENT_EVENT));
}
